import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Building2, Trees } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useConsultation } from "../context/ConsultationContext";
import { cn } from "../lib/utils";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Visa Services", to: "/visa-services" },
  { label: "Banking & Corporate", to: "/banking" },
  { label: "Resource Center", to: "/resources" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-[#002366]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      )}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-2 group">
          <span className="font-heading text-2xl md:text-[26px] font-light tracking-tight text-white">
            Start<span className="font-black tracking-tight">INC</span>
            <span className="text-[#6B8AFF]">.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink
            to="/"
            data-testid="nav-home"
            className={({ isActive }) =>
              cn(
                "font-heading text-sm tracking-wide text-white/85 hover:text-white link-underline",
                isActive && "text-white"
              )
            }
            end
          >
            Home
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-company-formation"
              className="inline-flex items-center gap-1 font-heading text-sm tracking-wide text-white/85 hover:text-white outline-none"
            >
              Company Formation <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-72 bg-[#0A1128]/95 backdrop-blur-xl border border-white/10 text-white rounded-sm p-2"
            >
              <DropdownMenuItem
                data-testid="nav-mainland"
                asChild
                className="focus:bg-white/10 focus:text-white rounded-sm p-3"
              >
                <Link to="/company-formation/mainland" className="flex gap-3">
                  <Building2 className="w-5 h-5 text-[#6B8AFF] mt-0.5" />
                  <div>
                    <div className="font-heading font-semibold">Mainland Setup</div>
                    <div className="text-xs text-white/60 mt-0.5">
                      Trade anywhere in the UAE & beyond.
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="nav-freezone"
                asChild
                className="focus:bg-white/10 focus:text-white rounded-sm p-3"
              >
                <Link to="/company-formation/freezone" className="flex gap-3">
                  <Trees className="w-5 h-5 text-[#6B8AFF] mt-0.5" />
                  <div>
                    <div className="font-heading font-semibold">Freezone Setup</div>
                    <div className="text-xs text-white/60 mt-0.5">
                      0% corporate tax & 100% ownership.
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {NAV.slice(1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "font-heading text-sm tracking-wide text-white/85 hover:text-white link-underline",
                  isActive && "text-white"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-cta-button"
            onClick={() => open("other")}
            className="hidden md:inline-flex btn-gold text-sm"
          >
            Get a Free Consultation
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-sm border border-white/15 text-white"
            onClick={() => setMobileOpen((s) => !s)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0A1128]/98 backdrop-blur-xl border-t border-white/10"
        >
          <div className="container-x py-6 flex flex-col gap-2">
            {[{ label: "Home", to: "/" },
              { label: "Mainland Setup", to: "/company-formation/mainland" },
              { label: "Freezone Setup", to: "/company-formation/freezone" },
              ...NAV.slice(1)].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="font-heading text-white/90 py-3 border-b border-white/10"
              >
                {item.label}
              </Link>
            ))}
            <button
              data-testid="mobile-cta-button"
              onClick={() => open("other")}
              className="btn-gold mt-4"
            >
              Get a Free Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
